import isElectron from 'is-electron';
import {
  mapLanguageDiscriminatedListCoreToLegacy,
  mapLocalizedRecordCoreToLegacy,
} from '../../api/legacyCommonMappers';
import type { LoginForm } from '../../containers/Login/LegacyLogin';
import type { EmergencyType } from '../../containers/Alarm/AlarmSlice/types';
import { setItem } from '../../utils/storage';
import type { UserById } from '../userAPI/types';
import { ApiCore } from '../utils/core';
import type {
  ChangePasswordParams,
  UserInfoBindingModel,
  ForgetPasswordParams,
  ResetPasswordParams,
  SSOParams,
  Support,
  UserInfoViewModelApp,
  ValidateResetTokenParams,
  VerifyParams,
} from './types';
import type { AxiosResponse } from 'axios';

export const legacyAccountAPI = new ApiCore({
  getAll: true,
  getSingle: true,
  post: true,
  put: false,
  patch: true,
  remove: false,
  singleExtra: true,
  url: 'account',
});

const getClientType = () => (isElectron() ? 2 : 1); // 0: Mobile, 1: Web, 2: Desktop

export const getAntiForgeryToken = async (baseUrl?: string) => {
  const csrf = await legacyAccountAPI.performExtra<any>({
    method: 'GET',
    extraResource: 'AntiForgeryToken',
    baseUrl,
  });
  setItem('csrf', csrf.requestVerificationToken);
  return csrf.requestVerificationToken;
};

export const loginWithCode = async ({ code, email, password }: LoginForm) => {
  const csrfToken = await getAntiForgeryToken();

  return legacyAccountAPI.performExtra<UserInfoViewModelApp>({
    method: 'POST',
    extraResource: 'login',
    model: {
      code,
      email,
      password,
      clientType: getClientType(),
    },
    headers: { 'X-XSRF-Token': csrfToken },
  });
};

export const ssoLogin = async ({
  email,
  code,
  url,
  provider,
}: {
  email: string | null;
  code: string;
  url: string;
  provider?: string;
}) => {
  const csrfToken = await getAntiForgeryToken();

  return legacyAccountAPI.performExtra<UserInfoViewModelApp>({
    method: 'POST',
    extraResource: 'login',
    model: {
      email,
      oauth_code: code,
      redirect_url: url,
      sso_provider: provider,
      clientType: getClientType(),
    },
    headers: { 'X-XSRF-Token': csrfToken },
  });
};
export const fetchSsoUrl = async (url: string) => {
  return legacyAccountAPI.performExtra<SSOParams>({
    method: 'GET',
    extraResource: url,
  });
};

export const forgetPassword = async ({
  emailHash,
  baseUrl,
}: ForgetPasswordParams & { baseUrl: string }) => {
  const csrfToken = await getAntiForgeryToken(baseUrl);
  return legacyAccountAPI.performExtra<AxiosResponse<void>>({
    method: 'POST',
    extraResource: 'Password/Token',
    model: {
      emailHash,
    },
    headers: { 'X-XSRF-Token': csrfToken },
    baseUrl,
  });
};

export const validateResetToken = async ({
  userId,
  token,
  baseUrl,
}: ValidateResetTokenParams & { baseUrl: string }) => {
  return legacyAccountAPI.performExtra<{ ok?: boolean }>({
    method: 'GET',
    extraResource: `password/validatetoken`,
    params: { userId: userId, token: token },
    baseUrl,
  });
};

export const resetPassword = async (params: ResetPasswordParams, baseUrl: string) => {
  const csrfToken = await getAntiForgeryToken(baseUrl);

  return legacyAccountAPI.performExtra<{ ok?: boolean }>({
    method: 'POST',
    extraResource: `password/reset`,
    model: params,
    headers: { 'X-XSRF-Token': csrfToken },
    baseUrl,
  });
};

export const getSupportInfo = async () => {
  return legacyAccountAPI.performExtra<Support[]>({
    method: 'GET',
    extraResource: `support`,
  });
};

export const changePassword = async (params: ChangePasswordParams) => {
  const csrfToken = await getAntiForgeryToken();

  return legacyAccountAPI.performExtra<{ ok?: boolean }>({
    method: 'POST',
    extraResource: `ChangePassword`,
    model: params,
    headers: { 'X-XSRF-Token': csrfToken },
  });
};

export const updateProfileImage = async (model: Pick<UserInfoBindingModel, 'photoFileName'>) => {
  const csrfToken = await getAntiForgeryToken();
  return legacyAccountAPI.performExtra<UserById>({
    method: 'POST',
    model,
    extraResource: '',
    headers: { 'X-XSRF-Token': csrfToken },
  });
};

// Verify user password

export const verifyPassword = async (password: VerifyParams) => {
  const csrfToken = await getAntiForgeryToken();

  return legacyAccountAPI.performExtra<any>({
    method: 'POST',
    extraResource: 'validate-password',
    model: password,
    headers: { 'X-XSRF-Token': csrfToken },
  });
};

export const getEmergencyTypes = async () => {
  const response = await legacyAccountAPI.performExtra<EmergencyType[]>({
    method: 'GET',
    extraResource: 'EmergencyTypes',
  });

  return response?.map(emergencyType => ({
    ...emergencyType,
    Names: mapLanguageDiscriminatedListCoreToLegacy(emergencyType.Names),
    alarmTemplateType: mapLocalizedRecordCoreToLegacy(emergencyType.alarmTemplateType),
  }));
};
